import { Theme } from '@mui/material/styles'
import { OverridesStyleRules } from '@mui/material/styles/overrides'
import { colors, general } from './config'

/**
 * Flow specific MUI component overrides.
 *
 * @param theme
 * @returns
 */
export const flowOverrides = (theme: Theme): OverridesStyleRules => ({
  MuiSlider: {
    styleOverrides: {
      root: {
        height: 5,
      },
      rail: {
        boxShadow: 'inset -1px 1px 2px #aeaeae, inset 1px -1px 2px #ffffff',
        height: 5,
        borderRadius: 5,
        border: '1px solid #979797',
      },
      track: {
        boxShadow: 'inset -1px 1px 2px #568014, inset 1px -1px 2px #9ce824;',
        height: 5,
        borderRadius: 5,
        border: '1px solid',
        borderColor: theme.palette.primary.main,
      },
      mark: {
        height: 5,
        top: 14,
        color: colors.white,
      },
      thumb: {
        height: 16,
        width: 16,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      marginDense: {
        marginTop: 0,
        marginBottom: 0,
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        fontSize: theme.shape.inputField.fontSize,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: 14,
        color: theme.palette.text.primary,
      },
    },
  },
  MuiInputBase: {
    defaultProps: {
      // Needed to prevent adding a global style for every input field
      // see https://github.com/mui/material-ui/pull/29213
      disableInjectingGlobalStyles: true,
    },
    styleOverrides: {
      root: {
        minHeight: 32,
        // no max height because of the multiline inputs
        borderRadius: general.defaultBorderRadius,
        backgroundColor: theme.palette.background.paper,
        '&.Mui-disabled': {
          backgroundColor: theme.palette.grey.grey06,
        },
        input: {
          height: theme.shape.inputField.height,
          boxSizing: 'border-box',
        },
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        transform: 'translate(14px, 9px) scale(1)',
        fontSize: 14,
        backgroundColor: 'transparent',
        letterSpacing: 0.5,
        '&.Mui-focused': {
          color: theme.palette.primary.dark,
        },
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -6px) scale(0.78)',
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        [theme.breakpoints.down('sm')]: {
          padding: 0,
        },
        padding: 0,
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      gutters: {
        [theme.breakpoints.up('md')]: {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.white,
        '& .MuiSvgIcon-root': {
          color: colors.grey00,
        },
      },
      paperAnchorDockedLeft: {
        borderRight: 'none',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: general.defaultBorderRadius,
        fontSize: theme.shape.inputField.fontSize,
        borderColor: theme.palette.grey.grey05,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.dark,
          borderWidth: 2,
        },
      },
      multiline: {
        paddingRight: 0,
      },
      adornedEnd: {
        paddingRight: 0,
      },
      notchedOutline: {
        borderColor: theme.palette.grey.inputBorder,
      },
      disabled: {
        borderColor: theme.palette.grey.inputBorderDisabled,
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      positionEnd: {
        marginLeft: 0,
      },
      positionStart: {
        marginLeft: `-${theme.spacing(1)}`,
      },
      root: {
        color: theme.palette.grey.grey00,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        minHeight: 36,
        maxHeight: 36,
        textTransform: 'none',
        borderRadius: theme.shape.borderRadius,
        boxShadow: 'none',
        ...theme.typography.button,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
      },
      containedSecondary: {
        boxShadow: 'none',
        '&:active': {
          boxShadow: 'none',
        },
      },
      text: {
        ...theme.typography.overline,
      },
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        '& .MuiButton-root': {
          '&:first-of-type': {
            borderTopLeftRadius: theme.shape.customBorderRadius.tabButton,
            borderBottomLeftRadius: theme.shape.customBorderRadius.tabButton,
          },
          '&:last-of-type': {
            borderTopRightRadius: theme.shape.customBorderRadius.tabButton,
            borderBottomRightRadius: theme.shape.customBorderRadius.tabButton,
          },
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: 0,
        paddingLeft: 24,
      },
    },
  },
  MuiDialog: {
    MuiButton: {
      color: 'primary',
    },
    styleOverrides: {
      paper: {
        [theme.breakpoints.down('md')]: {
          borderRadius: general.defaultBorderRadius,
          height: 'auto',
          maxHeight: '90%',
          width: '90%',
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 27,
        backgroundColor: theme.palette.background.paper,
      },
      indicator: {
        height: 3,
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 27,
        textTransform: 'none',
        margin: '0 16px',
        minWidth: 0,
        padding: 0,
        paddingBottom: 4,
        fontSize: 16,
        [theme.breakpoints.up('md')]: {
          padding: 0,
          minWidth: 0,
        },
      },
    },
  },
  MuiPickerDTTabs: {
    styleOverrides: {
      tabs: {
        margin: 0,
        color: theme.palette.primary.contrastText,
      },
    },
  },
  MuiPickersModal: {
    styleOverrides: {
      dialogRoot: {
        padding: 0,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: theme.spacing(1),
        maxWidth: 48,
        maxHeight: 48,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        zIndex: 999,
        borderRadius: general.defaultBorderRadius,
        boxShadow: '0px 4px 5px rgba(0, 0, 0, 0.2)',
        padding: 6,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 0,
        whiteSpace: 'break-spaces',
      },
      popper: {
        zIndex: 1300,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: 'hsla(83, 73%, 41%, 1.00)',
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: 'inherit',
        marginRight: 0,
        '& svg': {
          fontSize: 20,
        },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        width: 32,
        height: 32,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        paddingTop: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        backgroundImage: 'unset',
        borderRadius: general.defaultBorderRadius,
        boxShadow: theme.effectStyles.shadowMain,
        '&.MuiTableHead-root,&.MuiTablePagination-root,&.MuiAlert-root,&.MuiTableBody-root,&.MuiPaper-elevation0': {
          boxShadow: 'none',
        },
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: general.defaultBorderRadius,
        paddingLeft: 0,
        paddingRight: 0,
        marginBottom: theme.spacing(1),
        '&:first-of-type,&:last-of-type': {
          borderRadius: general.defaultBorderRadius,
        },
        '&.Mui-expanded': {
          borderRadius: general.defaultBorderRadius,
        },
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        border: 0,
        borderRadius: 0,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: general.defaultBorderRadius,
        padding: '0 !important',
      },
      list: {
        backgroundColor: theme.palette.shades.shade04,
        '& .MuiMenuItem-root:hover': {
          backgroundColor: theme.palette.primary.light,
        },
        '& .MuiTypography-root': {
          fontSize: theme.shape.inputField.fontSize,
        },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        padding: 0,
        backgroundColor: theme.palette.shades.shade04,
      },
    },
  },
})
